export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    readonly details?: ValidationDetail[],
  ) {
    super(message);
  }
}

/** API error detail; TX-15 adds fix hints. */
export interface ValidationDetail {
  path: string;
  message: string;
  hint?: string;
  did_you_mean?: string;
  fix?: { op: string; [k: string]: unknown };
  severity?: 'warning';
}

async function request<T>(method: string, path: string, body?: unknown, extra?: Record<string, string>): Promise<T> {
  const res = await fetch(`/dashboard/v1${path}`, {
    method,
    credentials: 'same-origin',
    headers: {
      ...(body !== undefined ? { 'Content-Type': 'application/json' } : {}),
      // Required by the server for every mutation (CSRF guard).
      'X-MailOcean-CSRF': '1',
      ...(extra ?? {}),
    },
    body: body === undefined ? undefined : JSON.stringify(body),
  });
  const text = await res.text();
  const data = text ? JSON.parse(text) : null;
  if (!res.ok) {
    const e = data?.error ?? {};
    throw new ApiError(res.status, e.code ?? 'HTTP_ERROR', e.message ?? `HTTP ${res.status}`, e.details);
  }
  return data as T;
}

export const api = {
  get: <T>(path: string) => request<T>('GET', path),
  post: <T>(path: string, body: unknown = {}) => request<T>('POST', path, body),
  /** A POST with an Idempotency-Key (a repeated press or a retry returns the same result). */
  postIdem: <T>(path: string, body: unknown, key: string) => request<T>('POST', path, body, { 'Idempotency-Key': key }),
  patch: <T>(path: string, body: unknown) => request<T>('PATCH', path, body),
  put: <T>(path: string, body: unknown) => request<T>('PUT', path, body),
  del: <T>(path: string) => request<T>('DELETE', path),
};

// ---------- types mirrored from the API ----------

export type Role = 'owner' | 'admin' | 'developer' | 'viewer';

/** Workspace setup (the onboarding wizard, profile, domains, keys) is for owners and admins only. */
export const canSetUpWorkspace = (me: Me) => me.user.role === 'owner' || me.user.role === 'admin';
export const needsOnboarding = (me: Me) => !me.workspace.onboarding.done && canSetUpWorkspace(me);
export type OnboardingStep = 'profile' | 'domain' | 'api_key' | 'test_email' | 'webhook';

export interface Me {
  user: { id: string; email: string; name: string; role: Role; totp_enabled: boolean; mfa_enabled: boolean };
  workspace: {
    id: string;
    name: string;
    website: string | null;
    use_case: 'transactional' | 'marketing' | 'both' | null;
    monthly_volume: string | null;
    require_2fa: boolean;
    onboarding: { completed: OnboardingStep[]; steps: OnboardingStep[]; done: boolean };
    /** Set by MailOcean staff: paused = accepted and queued, suspended = the API refuses new emails. */
    sending?: { status: 'active' | 'paused' | 'suspended'; public_message: string | null; paused_streams: ('transactional' | 'marketing')[] };
  };
  platform: { sender_domain: string; delivery_provider: 'sandbox' | 'ses'; ai?: AiStatus };
  /** Read-only "view as customer" session of MailOcean support. */
  support?: { staff_name: string; expires_at: string } | null;
}

export interface DnsRecord {
  kind: 'spf' | 'dkim' | 'dmarc';
  type: 'TXT';
  host: string;
  fqdn: string;
  value: string;
  required: boolean;
  purpose: string;
}
export interface DnsCheck {
  ok: boolean;
  found: string[];
}
export interface Domain {
  id: string;
  name: string;
  status: 'pending' | 'verified';
  created_at: string;
  verified_at: string | null;
  last_checked_at: string | null;
  checks: Partial<Record<'spf' | 'dkim' | 'dmarc', DnsCheck>>;
  records: DnsRecord[];
}

export interface ApiKey {
  id: string;
  name: string;
  prefix: string;
  mode: 'test' | 'live';
  scopes: string[];
  created_at: string;
  last_used_at: string | null;
  revoked_at: string | null;
  key?: string;
}

export type EmailStatus =
  | 'queued'
  | 'suppressed'
  | 'sent'
  | 'delivery_unknown'
  | 'failed'
  | 'delivered'
  | 'bounced'
  | 'complained'
  | 'cancelled';

export interface EmailListItem {
  id: string;
  to: string[];
  from: string;
  subject: string;
  status: EmailStatus;
  stream: 'transactional' | 'marketing';
  tags: Record<string, string>;
  created_at: string;
}

export interface TimelineEvent {
  type: string;
  at: string;
  elapsed_ms: number;
  delta_ms: number;
  recipient: string | null;
  source: string;
  data: Record<string, unknown>;
}

export interface EmailDetail {
  id: string;
  status: EmailStatus;
  stream: 'transactional' | 'marketing';
  from: { email: string; name: string | null };
  to: string[];
  cc: string[];
  bcc: string[];
  suppressed_recipients: string[];
  template: { id: string; version: number } | null;
  reply_to: string | null;
  subject: string;
  tags: Record<string, string>;
  metadata: Record<string, unknown>;
  provider: string | null;
  provider_message_id: string | null;
  received_at: string;
  timeline: TimelineEvent[];
  attachments: { filename: string; content_type: string; content_id: string | null; size: number; content_purged: boolean }[];
}

export interface Stats {
  days: number;
  totals: Record<string, number> & { all: number };
  rates: { delivered: number; bounce: number; complaint: number };
  latency_ms: { p50: number | null; p95: number | null };
  series: { day: string; total: number; delivered: number; bounced: number; failed: number }[];
  domains: { total: number; verified: number };
  health: number;
  onboarded: boolean;
}

export interface Suppression {
  email: string;
  scope: 'all' | 'marketing';
  reason: string;
  source: string;
  created_at: string;
}

export interface Webhook {
  id: string;
  url: string;
  events: string[];
  format: 'mailocean' | 'sendgrid' | 'postmark';
  enabled: boolean;
  created_at: string;
  secret?: string;
}

export interface WebhookDelivery {
  id: string;
  event_type: string;
  state: 'PENDING' | 'DELIVERING' | 'DELIVERED' | 'FAILED';
  attempts: number;
  last_status: number | null;
  last_error: string | null;
  created_at: string;
  delivered_at: string | null;
  payload: unknown;
}

export const WEBHOOK_EVENTS = [
  'email.sent',
  'email.delivered',
  'email.bounced',
  'email.soft_bounced',
  'email.complained',
  'email.failed',
  'email.delivery_unknown',
  'email.suppressed',
  'email.cancelled',
  // A journey's «Вебхук» step goes only to the endpoint the step names (docs/journeys/SPEC.md §8).
  'journey.webhook',
];

export interface TeamMember {
  user_id: string;
  email: string;
  name: string;
  role: Role;
  joined_at: string;
  totp_enabled: boolean;
}
export interface PendingInvite {
  id: string;
  email: string;
  role: Role;
  created_at: string;
  expires_at: string;
}
export interface Team {
  members: TeamMember[];
  invites: PendingInvite[];
}
export interface InviteInfo {
  workspace: string;
  email: string;
  role: Role;
  invited_by: string | null;
  user_exists: boolean;
}
export interface Workspace {
  id: string;
  name: string;
  role: Role;
  current: boolean;
}
export interface Template {
  id: string;
  name: string;
  alias: string;
  version: number;
  subject: string;
  html: string | null;
  text: string | null;
  variables: string[];
  created_at: string;
  updated_at: string;
}
export interface TemplateVersion {
  version: number;
  subject: string;
  created_by: string | null;
  created_at: string;
}
export interface TemplatePreview {
  subject: string;
  html: string | null;
  text: string | null;
  missing: string[];
  variables: string[];
}

// ---------- TX-15: Log Detective & Code2Email ----------

export interface AiStatus {
  enabled: boolean;
  provider: string | null;
  model: string | null;
}

export type Severity = 'critical' | 'warning' | 'info';

export interface Evidence {
  kind: 'message' | 'event' | 'domain' | 'suppression' | 'outbox';
  id: string;
  message_id?: string;
  at?: string;
  label: string;
  href: string;
}

export interface Finding {
  id: string;
  severity: Severity;
  title: string;
  detail: string;
  actions: string[];
  evidence: Evidence[];
  evidence_total: number;
  metrics?: Record<string, number | string>;
  records?: { type: 'TXT'; host: string; fqdn: string; value: string; purpose: string }[];
}

export interface DetectiveAnswer {
  question: string;
  parsed: {
    provider: { id: string; label: string } | null;
    recipient_domain: string | null;
    focus: string[];
    window: { since: string; until: string; label: string; source: 'question' | 'param' | 'default' };
    message_id: string | null;
  };
  stats: {
    messages: number;
    handed_off: number;
    delivered: number;
    hard_bounces: number;
    soft_bounces: number;
    complaints: number;
    failed: number;
    unknown: number;
    suppressed: number;
    bounce_rate: number;
    complaint_rate: number;
    truncated: boolean;
    by_provider: { id: string; label: string; messages: number; handed_off: number; hard_bounces: number; complaints: number }[];
  };
  findings: Finding[];
  /** Problems outside the question's focus: named, not hidden. */
  other_findings: Finding[];
  /** What the analysis cannot see (e.g. the folder an email landed in). */
  notes: string[];
  summary: string;
  narration: { mode: 'deterministic' | 'llm'; text: string; model: string | null; notice: string };
  generated_at: string;
}

export interface Draft {
  id: string;
  name: string;
  alias: string;
  subject: string;
  html: string | null;
  text: string | null;
  variables: string[];
  sample_variables: Record<string, unknown>;
  archetype: { id: string; label: string } | null;
  alternatives: { id: string; label: string }[];
  language: 'ru' | 'en';
  brand: { product_name: string | null; color: string | null };
  generator: { mode: 'deterministic' | 'llm'; model: string | null; notice: string | null };
  notes: string[];
  snippets: Record<string, string>;
  created_at: string;
  published: { template_id: string; version: number; at: string | null } | null;
}

export interface DraftListItem {
  id: string;
  name: string;
  alias: string;
  subject: string;
  archetype: string;
  mode: 'deterministic' | 'llm';
  created_at: string;
  published: { template_id: string; version: number; at: string | null } | null;
}

export interface Archetype {
  id: string;
  label: { ru: string; en: string };
  alias: string;
  variables: string[];
}

// ---------- «Маяк» B3: billing, alerts, sessions, devices ----------

export interface Plan {
  id: string;
  name: string;
  price_cents: number;
  currency: string;
  included_emails: number;
  daily_limit: number | null;
  max_domains: number | null;
  log_retention_days: number;
  overage_cents_per_1000: number | null;
  features: string[];
}

export type InvoiceStatus = 'draft' | 'open' | 'paid' | 'void' | 'uncollectible';

export interface Invoice {
  id: string;
  number: string | null;
  status: InvoiceStatus;
  status_label: string;
  currency: string;
  subtotal_cents: number;
  tax_cents: number;
  total_cents: number;
  paid_cents: number;
  amount_due_cents: number;
  period_start: string | null;
  period_end: string | null;
  issued_at: string | null;
  due_at: string | null;
  overdue: boolean;
  paid_at: string | null;
  voided_at: string | null;
  notes: string | null;
  created_at: string;
}

export interface PaymentInstructions {
  kind: 'bank_transfer' | 'link';
  text?: string;
  url?: string;
}

export interface InvoiceDetail extends Invoice {
  tenant_name: string | null;
  lines: { id: string; kind: 'plan' | 'overage' | 'manual' | 'discount'; description: string; quantity: number; unit_cents: number; amount_cents: number }[];
  payments: { id: string; amount_cents: number; currency: string; status: string; method: string | null; created_at: string }[];
  payment_instructions: PaymentInstructions | null;
}

export interface BillingView {
  plan: Plan | null;
  subscription: {
    id: string;
    plan_id: string;
    plan_name: string;
    status: 'trialing' | 'active' | 'past_due' | 'canceled';
    price_cents: number;
    currency: string;
    current_period_start: string;
    current_period_end: string;
    trial_ends_at: string | null;
  } | null;
  usage: { month: string; accepted_emails: number; test_emails: number; included_emails: number; over_quota: boolean; updated_at: string | null };
  /** false for members other than owner/admin: invoices and payment details are not sent to them. */
  finance_visible: boolean;
  invoices: Invoice[];
  payment_instructions: PaymentInstructions | null;
  plans: Plan[];
  provider: string;
}

export interface Alert {
  kind: string;
  severity: 'critical' | 'warning' | 'info';
  title: string;
  body: string;
  link: string;
  at: string;
}

export interface SessionItem {
  session_ref: string;
  client: 'web' | 'mobile';
  device: string | null;
  ip: string | null;
  created_at: string | null;
  last_seen_at: string | null;
  current: boolean;
}

export interface DeviceItem {
  id: string;
  platform: 'ios' | 'android';
  has_push_token: boolean;
  app_version: string;
  os_version: string | null;
  model: string | null;
  last_seen_at: string;
  current: boolean;
}

// ---------- Ocean Data (Build 3 J1) ----------

export type AttributeType = 'string' | 'number' | 'boolean' | 'date' | 'datetime' | 'string_list';
export type ConsentState = { marketing?: { status: 'granted' | 'withdrawn'; source: string; at: string } };

export interface Contact {
  id: string;
  external_id: string | null;
  email: string | null;
  attributes: Record<string, unknown>;
  consent: ConsentState;
  timezone: string | null;
  locale: string | null;
  created_at: string;
  updated_at: string;
  last_event_at: string | null;
  suppressed?: { all: boolean; marketing: boolean };
  suppressions?: { scope: 'all' | 'marketing'; reason: string; source: string; created_at: string }[];
}

export interface ContactEvent {
  id: string;
  contact_id: string;
  name: string;
  properties: Record<string, unknown>;
  idempotency_key: string | null;
  source: 'api' | 'dashboard' | 'system';
  occurred_at: string;
  received_at: string;
}

export interface ConsentRecord {
  id: number;
  stream: 'marketing';
  status: 'granted' | 'withdrawn';
  source: string;
  proof: Record<string, unknown>;
  occurred_at: string;
  recorded_at: string;
  recorded_by: string;
}

export interface AttributeDef {
  key: string;
  type: AttributeType;
  label: string | null;
  created_at: string;
}

export interface Segment {
  id: string;
  key: string;
  name: string;
  conditions: unknown;
  revision: number;
  last_count: number | null;
  last_refreshed_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface SegmentPreview {
  count: number;
  excluded: { no_email: number; suppressed: number; withdrawn: number; no_consent: number };
  eligible: number;
  sample: { id: string; email_masked: string | null }[];
  computed_at: string;
  stream: 'marketing' | 'transactional';
}

export interface Paged<T> {
  items: T[];
  next_cursor: string | null;
}
